import { Heading, Hidden, Section, Stack } from "@origin-digital/ods-core";
import React, { ReactElement } from "react";

import {
  ComponentBaseProps,
  HeadingComponentVariants,
} from "@origin-digital/ods-types";
import styled from "styled-components";
import { isNative } from "@origin-digital/platform-helpers";
import { ActionBarMobile, getMobileNavButton } from "./ActionBarMobile";
import { Box } from "./Box";
import { HelpSupportButton } from "./HelpSupportButton";
import { NavTitle } from "./NavTitle";

export interface Premise {
  premiseName: string;
  premiseId: string;
}

export type MobileNavButtonIconType = "back" | "close";
export type MobileNavButtonType = "none" | MobileNavButtonIconType;

export interface ActionBarProps extends ComponentBaseProps {
  title?: string;
  headingComponent?: HeadingComponentVariants;
  mobileNavButton?: MobileNavButtonType;
  onClickMobileNavButton?: (
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void | boolean;
  desktopVariant?: "default" | "slim";
  propertySelector?: ReactElement;
  showPropertySelector?: boolean;
  showAllProperties?: boolean;
  showServiceSelector?: boolean;
  serviceSelector?: ReactElement;
  onClickHelpSupportButton?: () => void;
  secondaryColor?: boolean;
}

const ViewContainer = styled(Box)<{
  desktopVariant: ActionBarProps["desktopVariant"];
}>`
  display: flex;
  align-items: center;

  @media (min-width: ${(p) => p.theme.breakpoints.lg}) {
    height: 64px;
  }
`;

const StyledHidden = styled(Hidden)`
  width: 100%;
`;

const OuterBox = styled(Box)`
  overflow: hidden;
  position: relative;
  z-index: 1000;
`;

const TitleContainer = styled(Box)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  && h1 {
    overflow: hidden;
    font-size: ${({ theme }) => `${theme.typography.body1.fontSize}px`};
  }
`;

const PropertySelectorContainer = styled(Box)`
  flex: 0 0 300px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  max-width: 300px;
`;

const HelpSupportButtonContainer = styled(Box)`
  flex: 0 0 300px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledBox = styled(Box)<{ secondaryColor?: boolean }>`
  background-color: ${({ theme, secondaryColor }) =>
    secondaryColor
      ? theme.palette.background.secondary
      : theme.palette.backgroundColor.primary};
`;

export const ActionBar: React.FC<ActionBarProps> = ({
  title,
  headingComponent = "h1",
  mobileNavButton = "none",
  onClickMobileNavButton,
  "data-id": dataId = "action-bar",
  desktopVariant = "default",
  // @ts-ignore currently unused, for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showAllProperties = true,
  showPropertySelector = false,
  propertySelector,
  showServiceSelector = false,
  serviceSelector,
  onClickHelpSupportButton,
  secondaryColor = false,
}) => (
  <OuterBox>
    <StyledBox
      data-id={dataId}
      display="flex"
      flexDirection="column"
      secondaryColor={secondaryColor}
    >
      <Box display="flex" width="full">
        <ViewContainer
          desktopVariant={desktopVariant}
          display="flex"
          width="full"
        >
          <StyledHidden below="lg">
            <Section paddingY="none" backgroundColor="transparent">
              <Box display="flex" justifyContent="spaceBetween">
                {desktopVariant === "slim" ? (
                  <Stack>
                    <NavTitle
                      mobileNavButton={mobileNavButton}
                      headingComponent={headingComponent}
                      onClickMobileNavButton={onClickMobileNavButton}
                      title={title}
                      desktopVariant={desktopVariant}
                      isDesktop={true}
                      showPropertySelector={showPropertySelector}
                      propertySelector={propertySelector}
                      onClickHelpSupportButton={onClickHelpSupportButton}
                      secondaryColor={secondaryColor}
                    />
                  </Stack>
                ) : (
                  <>
                    {isNative() &&
                      getMobileNavButton(
                        mobileNavButton,
                        onClickMobileNavButton,
                        true,
                        desktopVariant,
                        showPropertySelector
                      )}
                    <PropertySelectorContainer>
                      {showPropertySelector && propertySelector}
                    </PropertySelectorContainer>
                    <TitleContainer>
                      {title ? (
                        <Heading
                          variant="h3"
                          data-id="actionbar-heading-desktop"
                          component={headingComponent}
                          truncate
                        >
                          {title}
                        </Heading>
                      ) : null}
                    </TitleContainer>
                    <HelpSupportButtonContainer>
                      {onClickHelpSupportButton && (
                        <HelpSupportButton
                          onClick={onClickHelpSupportButton}
                          secondaryColor={secondaryColor}
                        />
                      )}
                    </HelpSupportButtonContainer>
                  </>
                )}
              </Box>
            </Section>
          </StyledHidden>
          <StyledHidden above="lg">
            <ActionBarMobile
              title={title}
              headingComponent={headingComponent}
              mobileNavButton={mobileNavButton}
              onClickMobileNavButton={onClickMobileNavButton}
              showPropertySelector={showPropertySelector}
              propertySelector={propertySelector}
              onClickHelpSupportButton={onClickHelpSupportButton}
              secondaryColor={secondaryColor}
            />
          </StyledHidden>
        </ViewContainer>
      </Box>
      {showPropertySelector && showServiceSelector && serviceSelector}
    </StyledBox>
  </OuterBox>
);

ActionBar.displayName = "ActionBar";
